import { MaybeRef, toReactive, useArrayIncludes } from "@vueuse/core";
import {
    computed,
    MaybeRefOrGetter,
    ref,
    toRaw,
    toRefs,
    toValue,
    watch,
    WritableComputedRef,
} from "vue";

export const refChildren = <T extends object>(r: MaybeRef<T>) =>
    toRefs(toReactive<T>(r));

export function refProp<T extends {}, K extends keyof T>(
    r: MaybeRefOrGetter<T>,
    key: MaybeRefOrGetter<K>,
): WritableComputedRef<T[K]>;

export function refProp<T extends {}, K extends keyof T>(
    refObj: MaybeRef<T | null>,
    key: MaybeRefOrGetter<K>,
): WritableComputedRef<T[K] | null> {
    return computed({
        get: () => {
            const k = toValue(key);
            const val = toValue(refObj);
            return val != null ? val[k] : null;
        },
        set: (v: T[K]) => {
            const k = toValue(key);
            const val = toValue(refObj);
            if (val != null) {
                val[k] = toRaw(v);
            }
        },
    });
}

export function copyOnWriteRef<T>(refObj: MaybeRefOrGetter<T>) {
    const val = ref<T>(toValue(refObj));

    watch(
        () => toValue(refObj),
        (newVal) => {
            val.value = newVal;
        },
    );

    return val;
}

export function useItemInArray<T>(
    items: MaybeRefOrGetter<T[]>,
    x: T,
): WritableComputedRef<boolean> {
    const exists = useArrayIncludes(items, x);

    return computed({
        get: () => exists.value,
        set: (val: boolean) => {
            const arr = toValue(items);

            if (!exists.value && val) {
                arr.push(x);
            } else if (exists.value && !val) {
                arr.splice(arr.indexOf(x), 1);
            }
        },
    });
}
